import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  LinearProgress,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Fade,
  Zoom,
  useTheme,
  alpha,
  ButtonGroup,
  Button,
  Badge,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Assessment as AssessmentIcon,
  Savings as SavingsIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  Lightbulb as LightbulbIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Speed as SpeedIcon,
  TrackChanges as TargetIcon,
  Notifications as NotificationsIcon,
  AutoGraph as AutoGraphIcon,
  Psychology as PsychologyIcon,
  Timeline as TimelineIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { Line, Doughnut, Bar, Area } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  BarElement,
  Filler,
} from 'chart.js';
import { format, subDays, startOfMonth, endOfMonth, parseISO } from 'date-fns';

// Import Redux actions
import { fetchDashboardAnalytics, fetchSpendingPatterns, fetchFinancialInsights } from '../store/slices/analyticsSlice';
import { fetchTransactions } from '../store/slices/transactionSlice';
import { fetchBudgets, selectBudgetPerformance, selectOverallBudgetHealth } from '../store/slices/budgetSlice';
import { fetchGoals, selectGoals } from '../store/slices/goalSlice';
import { fetchNotifications, selectUnreadCount } from '../store/slices/notificationSlice';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  BarElement,
  Filler
);

const EnhancedDashboard = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  
  // Redux state
  const analytics = useSelector(state => state.analytics.dashboard);
  const spendingPatterns = useSelector(state => state.analytics.spendingPatterns);
  const insights = useSelector(state => state.analytics.insights);
  const transactions = useSelector(state => state.transactions.transactions);
  const budgetPerformance = useSelector(selectBudgetPerformance);
  const budgetHealth = useSelector(selectOverallBudgetHealth);
  const goals = useSelector(selectGoals);
  const unreadNotifications = useSelector(selectUnreadCount);
  const loading = useSelector(state => state.analytics.loading);

  // Local state
  const [timeframe, setTimeframe] = useState('month');
  const [selectedCard, setSelectedCard] = useState(null);

  // Fetch data on component mount
  useEffect(() => {
    dispatch(fetchDashboardAnalytics(timeframe));
    dispatch(fetchSpendingPatterns());
    dispatch(fetchFinancialInsights());
    dispatch(fetchTransactions());
    dispatch(fetchBudgets());
    dispatch(fetchGoals());
    dispatch(fetchNotifications());
  }, [dispatch, timeframe]);

  // Calculate real-time financial metrics
  const financialMetrics = useMemo(() => {
    const currentMonth = new Date();
    const startOfCurrentMonth = startOfMonth(currentMonth);
    const endOfCurrentMonth = endOfMonth(currentMonth);

    const currentMonthTransactions = transactions.filter(t => {
      const transactionDate = parseISO(t.transaction_date);
      return transactionDate >= startOfCurrentMonth && transactionDate <= endOfCurrentMonth;
    });

    const income = currentMonthTransactions
      .filter(t => t.transaction_type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);

    const expenses = currentMonthTransactions
      .filter(t => t.transaction_type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);

    const netIncome = income - expenses;
    const savingsRate = income > 0 ? ((netIncome / income) * 100) : 0;

    return { income, expenses, netIncome, savingsRate, transactionCount: currentMonthTransactions.length };
  }, [transactions]);

  // Financial Health Score calculation
  const healthScore = useMemo(() => {
    let score = 100;
    
    // Deduct for negative net income
    if (financialMetrics.netIncome < 0) score -= 30;
    
    // Deduct for low savings rate
    if (financialMetrics.savingsRate < 10) score -= 20;
    else if (financialMetrics.savingsRate < 20) score -= 10;
    
    // Deduct for budget performance
    if (budgetHealth.score < 70) score -= 20;
    
    // Add for active goals
    const activeGoals = goals.filter(g => g.status === 'active').length;
    score += Math.min(activeGoals * 5, 20);

    return Math.max(0, Math.min(100, score));
  }, [financialMetrics, budgetHealth, goals]);

  // Chart configurations
  const expenseVsIncomeData = {
    labels: ['Income', 'Expenses', 'Savings'],
    datasets: [{
      data: [
        financialMetrics.income,
        financialMetrics.expenses,
        Math.max(0, financialMetrics.netIncome)
      ],
      backgroundColor: [
        theme.palette.success.main,
        theme.palette.error.main,
        theme.palette.primary.main,
      ],
      borderWidth: 0,
      hoverOffset: 4,
    }],
  };

  const spendingTrendData = {
    labels: analytics.trends?.map(t => format(parseISO(t.date), 'MMM dd')) || [],
    datasets: [{
      label: 'Daily Expenses',
      data: analytics.trends?.map(t => t.expenses) || [],
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      tension: 0.4,
      fill: true,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: alpha(theme.palette.divider, 0.1),
        },
      },
      x: {
        grid: {
          color: alpha(theme.palette.divider, 0.1),
        },
      },
    },
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    hover: { scale: 1.02, transition: { duration: 0.2 } },
  };

  const MetricCard = ({ title, value, subtitle, icon, color, trend, onClick }) => (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      onClick={onClick}
    >
      <Card
        sx={{
          height: '100%',
          cursor: onClick ? 'pointer' : 'default',
          background: `linear-gradient(135deg, ${alpha(color, 0.1)}, ${alpha(color, 0.05)})`,
          border: `1px solid ${alpha(color, 0.2)}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: `0 8px 30px ${alpha(color, 0.3)}`,
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>
              {icon}
            </Avatar>
            {trend && (
              <Chip
                icon={trend > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                label={`${Math.abs(trend)}%`}
                color={trend > 0 ? 'success' : 'error'}
                size="small"
              />
            )}
          </Box>
          <Typography variant="h4" fontWeight="bold" color={color}>
            {value}
          </Typography>
          <Typography variant="h6" color="text.primary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </CardContent>
      </Card>
    </motion.div>
  );

  const InsightCard = ({ insight, index }) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Alert
        severity={insight.type}
        icon={insight.type === 'info' ? <LightbulbIcon /> : undefined}
        sx={{
          mb: 1,
          borderRadius: 2,
          '& .MuiAlert-message': {
            fontSize: '0.9rem',
          },
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          {insight.title}
        </Typography>
        {insight.message}
      </Alert>
    </motion.div>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <AnimatePresence>
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                 Financial Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Welcome back! Here's your financial overview for {format(new Date(), 'MMMM yyyy')}
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" gap={2}>
              {/* Timeframe Selector */}
              <ButtonGroup variant="outlined" size="small">
                {['week', 'month', 'quarter', 'year'].map((period) => (
                  <Button
                    key={period}
                    variant={timeframe === period ? 'contained' : 'outlined'}
                    onClick={() => setTimeframe(period)}
                  >
                    {period.charAt(0).toUpperCase() + period.slice(1)}
                  </Button>
                ))}
              </ButtonGroup>

              {/* Notifications */}
              <Tooltip title="Notifications">
                <IconButton>
                  <Badge badgeContent={unreadNotifications} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </motion.div>

        {/* Financial Health Score */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <Card
            sx={{
              mb: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              color: 'white',
            }}
          >
            <CardContent>
              <Grid container alignItems="center" spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" gutterBottom>
                    Financial Health Score
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Typography variant="h2" fontWeight="bold">
                      {healthScore}
                    </Typography>
                    <SpeedIcon sx={{ fontSize: 40 }} />
                  </Box>
                  <Typography variant="body1">
                    {healthScore >= 80 ? 'Excellent' : healthScore >= 60 ? 'Good' : healthScore >= 40 ? 'Fair' : 'Needs Improvement'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box position="relative" display="inline-flex">
                    <CircularProgress
                      variant="determinate"
                      value={healthScore}
                      size={120}
                      thickness={6}
                      sx={{ color: 'white' }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="h4" component="div" color="white" fontWeight="bold">
                        {healthScore}%
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </motion.div>

        {/* Key Metrics Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title="Total Income"
              value={`$${financialMetrics.income.toLocaleString()}`}
              subtitle="This month"
              icon={<TrendingUpIcon />}
              color={theme.palette.success.main}
              trend={15}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title="Total Expenses"
              value={`$${financialMetrics.expenses.toLocaleString()}`}
              subtitle="This month"
              icon={<TrendingDownIcon />}
              color={theme.palette.error.main}
              trend={-8}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title="Net Income"
              value={`$${financialMetrics.netIncome.toLocaleString()}`}
              subtitle="This month"
              icon={<AccountBalanceIcon />}
              color={financialMetrics.netIncome >= 0 ? theme.palette.success.main : theme.palette.error.main}
              trend={22}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title="Savings Rate"
              value={`${financialMetrics.savingsRate.toFixed(1)}%`}
              subtitle="Of total income"
              icon={<SavingsIcon />}
              color={theme.palette.primary.main}
              trend={5}
            />
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} mb={4}>
          {/* Expense vs Income Chart */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              <Card sx={{ height: 400 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Income vs Expenses
                  </Typography>
                  <Box height={300}>
                    <Doughnut data={expenseVsIncomeData} options={{ ...chartOptions, maintainAspectRatio: false }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Spending Trend Chart */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              <Card sx={{ height: 400 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Spending Trend
                  </Typography>
                  <Box height={300}>
                    <Line data={spendingTrendData} options={{ ...chartOptions, maintainAspectRatio: false }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Budget Performance & Goals */}
        <Grid container spacing={3} mb={4}>
          {/* Budget Performance */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                    <TargetIcon />
                    Budget Performance
                  </Typography>
                  {budgetPerformance.map((budget, index) => (
                    <Box key={budget.id} mb={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" fontWeight="medium">
                          {budget.name}
                        </Typography>
                        <Chip
                          label={budget.status}
                          color={
                            budget.status === 'good' ? 'success' :
                            budget.status === 'warning' ? 'warning' : 'error'
                          }
                          size="small"
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={budget.utilization}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: alpha(theme.palette.grey[300], 0.3),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: 
                              budget.status === 'good' ? theme.palette.success.main :
                              budget.status === 'warning' ? theme.palette.warning.main :
                              theme.palette.error.main,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        ${budget.spent_amount.toLocaleString()} / ${budget.amount.toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Goals Progress */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                    <TargetIcon />
                    Goals Progress
                  </Typography>
                  {goals.slice(0, 3).map((goal, index) => (
                    <Box key={goal.id} mb={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" fontWeight="medium">
                          {goal.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {goal.progress_percentage.toFixed(1)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={goal.progress_percentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: alpha(theme.palette.grey[300], 0.3),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: theme.palette.primary.main,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        ${goal.current_amount.toLocaleString()} / ${goal.target_amount.toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* AI Insights & Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
        >
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <PsychologyIcon />
                AI-Powered Insights & Recommendations
              </Typography>
              <Grid container spacing={2}>
                {insights.recommendations?.slice(0, 6).map((insight, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <InsightCard insight={insight} index={index} />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    </Container>
  );
};

export default EnhancedDashboard; 